import { useEffect, useRef } from "react";

const EASE = "cubic-bezier(0.22, 1, 0.36, 1)";

// Scroll/entrance reveal via IntersectionObserver + direct DOM (no React state).
export const Reveal = ({ children, delay = 0, y = 30, className = "", as: Tag = "div", ...rest }) => {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.opacity = "0";
    el.style.transform = `translateY(${y}px)`;
    el.style.transition = `opacity 0.9s ${EASE} ${delay}s, transform 0.9s ${EASE} ${delay}s`;
    el.style.willChange = "opacity, transform";

    const show = () => {
      el.style.opacity = "1";
      el.style.transform = "translateY(0)";
    };

    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          show();
          io.disconnect();
        }
      },
      { threshold: 0.12, rootMargin: "0px 0px -40px 0px" }
    );
    io.observe(el);
    // Safety fallback in case IO doesn't fire.
    const t = setTimeout(() => {
      const r = el.getBoundingClientRect();
      if (r.top < window.innerHeight && r.bottom > 0) show();
    }, 1200);

    return () => {
      io.disconnect();
      clearTimeout(t);
    };
  }, [delay, y]);

  return (
    <Tag ref={ref} className={className} {...rest}>
      {children}
    </Tag>
  );
};

// Masked line-by-line reveal via direct DOM (no React state).
export const MaskedLines = ({ lines, className = "", lineClassName = "", delay = 0, mode = "view" }) => {
  const ref = useRef(null);

  useEffect(() => {
    const wrap = ref.current;
    if (!wrap) return;
    const inners = wrap.querySelectorAll("[data-ml-line]");
    inners.forEach((el, i) => {
      el.style.display = "block";
      el.style.transform = "translateY(115%)";
      el.style.transition = `transform 1s ${EASE} ${delay + i * 0.12}s`;
      el.style.willChange = "transform";
    });
    const show = () => inners.forEach((el) => (el.style.transform = "translateY(0)"));

    if (mode === "load") {
      const id = requestAnimationFrame(show);
      return () => cancelAnimationFrame(id);
    }

    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          show();
          io.disconnect();
        }
      },
      { threshold: 0.1, rootMargin: "0px 0px -30px 0px" }
    );
    io.observe(wrap);
    const t = setTimeout(() => {
      const r = wrap.getBoundingClientRect();
      if (r.top < window.innerHeight && r.bottom > 0) show();
    }, 1200);
    return () => {
      io.disconnect();
      clearTimeout(t);
    };
  }, [delay, mode]);

  return (
    <span ref={ref} className={className}>
      {lines.map((line, i) => (
        <span key={`${line}-${i}`} className="block overflow-hidden">
          <span data-ml-line className={`block ${lineClassName}`}>
            {line}
          </span>
        </span>
      ))}
    </span>
  );
};
